// Pure dice math. No React. No Firestore. Composable from anywhere.

export const STANDARD_DICE = [4, 6, 8, 10, 12, 20, 100];

function roll1(sides) {
  return Math.floor(Math.random() * sides) + 1;
}

/**
 * Resolve one roll request and produce a serialisable record.
 *
 * @param {Object}   req
 * @param {Object[]} req.dice       — [{sides, count}, ...]; e.g. [{sides:20,count:1}]
 * @param {number}   [req.modifier] — flat +/- to total
 * @param {string}   [req.mode]     — 'normal' | 'advantage' | 'disadvantage' (only meaningful when a d20 is rolled)
 * @returns {Object} record matching the diceRoll Firestore shape (no metadata).
 */
export function rollDice({ dice = [], modifier = 0, mode = 'normal' } = {}) {
  const groups = [];
  for (const { sides, count } of dice) {
    if (!sides || count <= 0) continue;
    // Advantage/disadvantage: roll `count` dice, keep only the highest (adv) or lowest (dis)
    if ((mode === 'advantage' || mode === 'disadvantage') && count > 0) {
      const results = [];
      for (let i = 0; i < count; i++) results.push(roll1(sides));
      const keptValue = mode === 'advantage' ? Math.max(...results) : Math.min(...results);
      groups.push({ sides, results, kept: [keptValue] });
    } else {
      const results = [];
      for (let i = 0; i < count; i++) results.push(roll1(sides));
      groups.push({ sides, results, kept: [...results] });
    }
  }
  const keptSum = groups.reduce((acc, g) => acc + g.kept.reduce((a, b) => a + b, 0), 0);
  const total = keptSum + (modifier || 0);

  // Crit / fumble: only when exactly one d20 was kept (typical d20 attack/check).
  let crit = null;
  const d20Groups = groups.filter((g) => g.sides === 20);
  const totalD20Kept = d20Groups.reduce((acc, g) => acc + g.kept.length, 0);
  if (totalD20Kept === 1) {
    const v = d20Groups[0].kept[0];
    if (v === 20) crit = 'crit';
    else if (v === 1) crit = 'fumble';
  }

  return { dice: groups, modifier: modifier || 0, mode, total, crit };
}

/** Human-readable formula like "2d20+5 (Adv)" for the log. */
export function formatFormula({ dice, modifier, mode }) {
  if (!dice || !dice.length) return '';
  // Group by sides for readability — sum counts of like dice
  const counts = new Map();
  for (const g of dice) counts.set(g.sides, (counts.get(g.sides) || 0) + g.kept.length);
  // For adv/dis the "kept" count reflects the kept die count, but the user-facing
  // formula should read like 1d20 (Adv), not 2d20. Use kept count.
  const parts = [...counts.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([sides, count]) => `${count}d${sides}`);
  let s = parts.join(' + ');
  if (modifier > 0) s += ` + ${modifier}`;
  else if (modifier < 0) s += ` − ${Math.abs(modifier)}`;
  if (mode === 'advantage')    s += ' (Adv)';
  if (mode === 'disadvantage') s += ' (Dis)';
  return s;
}
